import { POP_DEFAULT_ZONE_FIELDS } from "@adlywise/shared";

// Popunder settings of an rtb provider, as edited in ProviderForm. Kept as
// strings / booleans for the inputs; popSettingsPayload turns them into the
// API shape (providers.settings.pop).

export const DEFAULT_POP_FORM = {
  configured: false,
  enabled: false,
  firstPriceConfirmed: false,
  admFormat: "url",
  costOn: "burl",
  redirectTtlSec: "300",
  sendBtype: false,
  fallbackUrl: "",
  noticeIpAllowlist: "",
  noticeFailure: "retry",
  zoneFields: POP_DEFAULT_ZONE_FIELDS.join(", "),
  requireSchain: false,
};

const lines = (text) =>
  String(text ?? "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const popFormFromSettings = (pop) => {
  if (!pop || typeof pop !== "object") return { ...DEFAULT_POP_FORM };
  return {
    // Keys this form does not edit (e.g. regionMap) ride along untouched: the
    // API replaces settings.pop wholesale, so dropping them would erase them.
    stored: pop,
    configured: true,
    enabled: Boolean(pop.enabled),
    firstPriceConfirmed: pop.settlement === "first_price_clear",
    admFormat: pop.admFormat ?? "url",
    costOn: pop.costOn ?? "burl",
    redirectTtlSec: String(pop.redirectTtlSec ?? 300),
    sendBtype: Boolean(pop.sendBtype),
    fallbackUrl: pop.fallbackUrl ?? "",
    noticeIpAllowlist: (pop.noticeIpAllowlist ?? []).join("\n"),
    noticeFailure: pop.noticeFailure ?? "retry",
    zoneFields: (pop.zoneFields ?? POP_DEFAULT_ZONE_FIELDS).join(", "),
    requireSchain: Boolean(pop.requireSchain),
  };
};

// The value to send as settings.pop: an object when configured; null when it
// was configured before and is now switched off (the API keeps the stored pop
// settings when the key is absent, so removal must be explicit); undefined
// when it never was (omit the key).
export const popSettingsPayload = (form, wasConfigured) => {
  if (!form.configured) return wasConfigured ? null : undefined;
  const ttl = Number(form.redirectTtlSec);
  const zoneFields = lines(form.zoneFields);
  return {
    ...(form.stored ?? {}),
    enabled: Boolean(form.enabled),
    settlement: form.firstPriceConfirmed ? "first_price_clear" : "unconfirmed",
    admFormat: form.admFormat,
    costOn: form.costOn,
    redirectTtlSec: Number.isFinite(ttl) ? Math.round(ttl) : 300,
    sendBtype: Boolean(form.sendBtype),
    fallbackUrl: form.fallbackUrl.trim() || null,
    noticeIpAllowlist: lines(form.noticeIpAllowlist),
    noticeFailure: form.noticeFailure,
    zoneFields:
      zoneFields.length > 0 ? zoneFields : [...POP_DEFAULT_ZONE_FIELDS],
    requireSchain: Boolean(form.requireSchain),
  };
};

// Human-readable problems the API would reject; empty ⇒ submittable.
export const popFormErrors = (form) => {
  if (!form.configured) return [];
  const errors = [];
  if (form.enabled && !form.firstPriceConfirmed) {
    errors.push(
      "Popunder can only be enabled for a partner confirmed first-price with a clear-text price macro.",
    );
  }
  const ttl = Number(form.redirectTtlSec);
  if (!Number.isInteger(ttl) || ttl < 60 || ttl > 1800) {
    errors.push("Redirect lifetime must be 60–1800 seconds.");
  }
  for (const f of lines(form.zoneFields)) {
    if (!/^[a-z]+(\.[A-Za-z_]+){1,3}$/.test(f)) {
      errors.push(`Not a request field path: ${f}`);
    }
  }
  if (form.fallbackUrl.trim().length > 2000) {
    errors.push("Fallback URL is longer than 2000 characters.");
  }
  if (
    form.fallbackUrl.trim() &&
    !/^https?:\/\/[\x21-\x7e]+$/.test(form.fallbackUrl.trim())
  ) {
    errors.push("Fallback URL must be an http(s) URL of printable ASCII.");
  }
  for (const cidr of lines(form.noticeIpAllowlist)) {
    if (!/^[0-9a-fA-F:.]+\/\d{1,3}$/.test(cidr)) {
      errors.push(`Not a CIDR: ${cidr}`);
    }
  }
  return errors;
};
